import { Inject, Injectable, InjectionToken } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import {
  asapScheduler,
  combineLatest,
  EMPTY,
  forkJoin,
  from,
  Observable,
  of,
  Subject,
  throwError,
} from 'rxjs';
import {
  catchError,
  concatMap,
  defaultIfEmpty,
  distinctUntilChanged,
  filter,
  map,
  observeOn,
  switchMap,
  take,
  tap,
  toArray,
} from 'rxjs/operators';
import type {
  ISaveAssignedPipelinesCommand,
  ISaveCaseTypeCommand,
  ISaveLabelCommand,
  ISaveLabelConfigCommand,
  ISaveMethodTypeCommand,
  ISavePipelineCommand,
  ISaveResourceCommand,
} from '@protocols/use-cases';

import { AssignedMethodTypesState } from '../state/assigned-method-types.state';
import { AssignedPipelinesState } from '../state/assigned-pipelines.state';
import { AssignedResourcesState, ResourceContainer } from '../state/assigned-resources.state';
import { CaseTypeEditorState } from '../state/case-type-editor.state';
import { LabelConfigState, StagedLabelConfig } from '../state/label-config.state';
import { LabelData, LabelOverride, LabelState } from '../state/label.state';
import { MethodTypeEditorState } from '../state/method-type-editor.state';
import { PipelineEditorState } from '../state/pipeline-editor.state';
import { StagedCaseDataState } from '../state/staged-case-data.state';
import { ResourceDraftData, ResourceDraftOverride, StagedResourcesState } from '../state/staged-resources.state';
import {
  StagedMethodTypeDraft,
  StagedMethodTypeOverride,
  StagedMethodTypesState,
} from '../state/staged-method-types.state';
import { StagedPipelineDraft, StagedPipelineOverride, StagedPipelinesState } from '../state/staged-pipelines.state';
import { SaveProtocolCascadePhase, SaveProtocolCascadeState } from '../state/save-protocol-cascade.state';

export const CASCADE_SAVE_LABEL_COMMAND = new InjectionToken<ISaveLabelCommand>('CASCADE_SAVE_LABEL_COMMAND');
export const CASCADE_SAVE_LABEL_CONFIG_COMMAND = new InjectionToken<ISaveLabelConfigCommand>(
  'CASCADE_SAVE_LABEL_CONFIG_COMMAND'
);
export const CASCADE_SAVE_PIPELINE_COMMAND = new InjectionToken<ISavePipelineCommand>('CASCADE_SAVE_PIPELINE_COMMAND');
export const CASCADE_SAVE_RESOURCE_COMMAND = new InjectionToken<ISaveResourceCommand>('CASCADE_SAVE_RESOURCE_COMMAND');
export const CASCADE_SAVE_METHOD_TYPE_COMMAND = new InjectionToken<ISaveMethodTypeCommand>(
  'CASCADE_SAVE_METHOD_TYPE_COMMAND'
);
export const CASCADE_SAVE_ASSIGNED_PIPELINES_COMMAND = new InjectionToken<ISaveAssignedPipelinesCommand>(
  'CASCADE_SAVE_ASSIGNED_PIPELINES_COMMAND'
);
export const CASCADE_SAVE_CASE_TYPE_COMMAND = new InjectionToken<ISaveCaseTypeCommand>(
  'CASCADE_SAVE_CASE_TYPE_COMMAND'
);

/*
 * Per-row outcome of a save inside a fail-fast consolidation batch
 * (`_runLabelConsolidation`, `_runPipelineConsolidation`). Rows resolve to this
 * instead of erroring so one rejection can't tear down the batch and orphan
 * in-flight siblings; `_runFailFastBatch` inspects the settled set to fail-fast.
 * `value` carries whatever the caller projects (a label's old→new uuid entry,
 * or nothing for a pipeline row).
 */
type BatchRowResult<T> = { readonly ok: true; readonly value: T } | { readonly ok: false; readonly error: Error };

/*
 * The slice of a staging State that `_runFailFastBatch` toggles when the batch
 * settles. Every consolidatable State exposes a `setSaving` updater.
 */
interface ISavingState {
  setSaving(saving: boolean): void;
}

/**
 * Save-protocol cascade — single orchestration class for the multi-step
 * persistence sequence of protocol entitities from case type to pipeline.
 */
@Injectable()
export class SaveProtocolCascadeEffects {
  constructor(
    private readonly cascadeState: SaveProtocolCascadeState,
    private readonly pipelineEditorState: PipelineEditorState,
    private readonly labelState: LabelState,
    private readonly labelConfigState: LabelConfigState,
    @Inject(CASCADE_SAVE_LABEL_COMMAND) private readonly saveLabelCommand: ISaveLabelCommand,
    @Inject(CASCADE_SAVE_PIPELINE_COMMAND) private readonly savePipelineCommand: ISavePipelineCommand,
    @Inject(CASCADE_SAVE_LABEL_CONFIG_COMMAND) private readonly saveLabelConfigCommand: ISaveLabelConfigCommand,
    private readonly stagedResourcesState: StagedResourcesState,
    private readonly assignedResourcesState: AssignedResourcesState,
    @Inject(CASCADE_SAVE_RESOURCE_COMMAND) private readonly saveResourceCommand: ISaveResourceCommand,
    private readonly stagedPipelinesState: StagedPipelinesState,
    private readonly assignedPipelinesState: AssignedPipelinesState,
    private readonly methodTypeEditorState: MethodTypeEditorState,
    @Inject(CASCADE_SAVE_METHOD_TYPE_COMMAND) private readonly saveMethodTypeCommand: ISaveMethodTypeCommand,
    @Inject(CASCADE_SAVE_ASSIGNED_PIPELINES_COMMAND)
    private readonly saveAssignedPipelinesCommand: ISaveAssignedPipelinesCommand,
    private readonly stagedMethodTypesState: StagedMethodTypesState,
    private readonly assignedMethodTypesState: AssignedMethodTypesState,
    private readonly stagedCaseDataState: StagedCaseDataState,
    private readonly caseTypeEditorState: CaseTypeEditorState,
    @Inject(CASCADE_SAVE_CASE_TYPE_COMMAND) private readonly saveCaseTypeCommand: ISaveCaseTypeCommand
  ) {}

  // ============================================================================
  // One-shot completion signal
  // ============================================================================

  private readonly _cascadeCompleted$ = new Subject<void>();
  /**
   * Emits once the full cascade finishes successfully. Does not replay.
   */
  public readonly cascadeCompleted$: Observable<void> = this._cascadeCompleted$.asObservable();

  // ============================================================================
  // Aggregate saving signal
  // ============================================================================

  public readonly saving$: Observable<boolean> = combineLatest([
    this.cascadeState.phase$,
    this.labelState.saving$,
    this.pipelineEditorState.saving$,
    this.labelConfigState.saving$,
    this.stagedResourcesState.saving$,
    this.stagedPipelinesState.saving$,
    this.methodTypeEditorState.saving$,
    this.stagedMethodTypesState.saving$,
    this.stagedCaseDataState.saving$,
    this.caseTypeEditorState.saving$,
  ]).pipe(
    map(
      ([
        phase,
        labelSaving,
        pipelineSaving,
        labelConfigSaving,
        resourceSaving,
        stagedPipelinesSaving,
        methodTypeSaving,
        stagedMethodTypesSaving,
        caseDataSaving,
        caseTypeSaving,
      ]) =>
        (phase as SaveProtocolCascadePhase) !== 'idle' ||
        (labelSaving as boolean) ||
        (pipelineSaving as boolean) ||
        (labelConfigSaving as boolean) ||
        (resourceSaving as boolean) ||
        (stagedPipelinesSaving as boolean) ||
        (methodTypeSaving as boolean) ||
        (stagedMethodTypesSaving as boolean) ||
        (caseDataSaving as boolean) ||
        (caseTypeSaving as boolean)
    ),
    distinctUntilChanged()
  );

  // ============================================================================
  // Top-level entry methods — guarded by the cascade phase
  // ============================================================================

  /*
   * Case-type-rooted top-level flow — the outermost consolidation. A case type
   * owns no entities of its own beyond its data fields; its children are the
   * assigned method types, each of which may carry its own staged pipelines,
   * labels, label-configs and resources. So this flow:
   *
   *   1. prune staged method types by ASSIGNMENT (created-then-unassigned drafts
   *      and edits of unassigned persisted method types are discarded), and
   *      prune the staged sub-entities of dropped method types (their resource
   *      subtrees and their label-configs),
   *   2. consolidate the GLOBAL session ledgers ONCE — they hold staging for
   *      every method type at once: labels (+ eager uuid remap), the surviving
   *      staged pipelines (collecting the tmp→real id map), the resource walk,
   *   3. per surviving method type (sequential): save the ROW, remap its tmp id
   *      through every ledger, apply the pipeline id map to its slice, then
   *      replace its `ProtocolPipeline` join set,
   *   4. save the case-type ROW LAST (name/description/fields + the final real
   *      method-type ids), then the best-effort label-config tail.
   *
   * Short-circuits when any non-idle phase is in progress. The method-type
   * row+joins reuse a draft-driven sibling of `_runMethodTypeSaveAndJoins`; the
   * label/pipeline/resource/config phases reuse the very same helpers the
   * single-method-type flow uses.
   */
  public readonly consolidateCaseType = this.cascadeState.effect<void>((trigger$) =>
    trigger$.pipe(
      concatLatestFrom(() => this.cascadeState.phase$),
      filter(([, phase]) => phase === 'idle'),
      tap(() => this.cascadeState.setPhase('running-case-type')),
      concatLatestFrom(() => [
        this.stagedMethodTypesState.pendingMethodTypes$,
        this.stagedMethodTypesState.methodTypeOverrides$,
        this.assignedMethodTypesState.assignedIds$,
        this.assignedMethodTypesState.stagedUnassignedIds$,
        this.assignedResourcesState.containers$,
        this.stagedPipelinesState.pendingPipelines$,
        this.stagedPipelinesState.pipelineOverrides$,
        this.labelState.pendingLabels$,
        this.labelState.labelOverrides$,
        this.labelConfigState.stagedConfigs$,
      ]),
      switchMap(
        ([
          ,
          pendingMethodTypes,
          methodTypeOverrides,
          assignedMethodTypeIds,
          stagedUnassignedMethodTypeIds,
          containers,
          pendingPipelines,
          pipelineOverrides,
          pendingLabels,
          labelOverrides,
          stagedConfigs,
        ]) => {
          // (1) Prune the method types not assigned to the case type, plus the
          // staged sub-entities owned by the dropped ones.
          const { survivors, prunedPendingIds } = this._pruneStaleMethodTypeStaging(
            pendingMethodTypes,
            methodTypeOverrides,
            assignedMethodTypeIds
          );
          const survivingMethodTypeIds = survivors.map((survivor) => survivor.draft.id);
          this._pruneDroppedMethodTypeResourceSubtrees(containers, new Set(survivingMethodTypeIds));
          this._pruneStaleMethodTypeLabelConfigs(prunedPendingIds, stagedUnassignedMethodTypeIds, stagedConfigs);

          return this._collectSurvivingAssignedPipelineIds(survivingMethodTypeIds).pipe(
            switchMap((pipelineReferenceIds) => {
              // (1 cont.) Prune pipelines (by the union of surviving assignments),
              // then the labels they no longer reference, then those labels' configs.
              const survivingDrafts = this._pruneStalePipelineStaging(
                pendingPipelines,
                pipelineOverrides,
                pipelineReferenceIds
              );
              const prunedPendingUuids = this._pruneStaleLabelStaging(
                this._collectDraftLabelUuids(survivingDrafts),
                pendingLabels,
                labelOverrides
              );
              this._pruneStaleLabelConfigs(prunedPendingUuids, stagedConfigs);

              // (2) Global ledgers, consolidated once.
              return this._runLabelConsolidation().pipe(
                tap((consolidatedLabelMap) => {
                  this.stagedPipelinesState.remapLabelUuids(consolidatedLabelMap);
                  this.labelConfigState.remapLabelUuids(consolidatedLabelMap);
                }),
                switchMap(() =>
                  this._runPipelineConsolidationCollecting().pipe(
                    switchMap((pipelineIdMap) =>
                      this._runMethodTypeResourcePhase().pipe(
                        // (3) Per surviving method type: row + joins.
                        switchMap(() => this._runStagedMethodTypeLoop(survivors, pipelineIdMap)),
                        // (4) The case-type row LAST, then the config tail.
                        switchMap(() =>
                          this._runCaseTypeRowSave().pipe(switchMap(() => this._runLabelConfigConsolidation()))
                        )
                      )
                    )
                  )
                ),
                tap(() => {
                  this.cascadeState.setPhase('idle');
                  this._cascadeCompleted$.next();
                }),
                catchError(() => {
                  // Every fail-fast phase records its failure on its owner state
                  // before (re)throwing; here the cascade just returns to idle.
                  this.cascadeState.setPhase('idle');
                  return EMPTY;
                })
              );
            })
          );
        }
      )
    )
  );

  /*
   * Method-type-rooted top-level flow:
   *
   *   1. prune staged pipelines by ASSIGNMENT (staged entities no longer an
   *      active part of the protocol are NOT persisted — a created-then-
   *      unassigned draft and the edits of an unassigned persisted pipeline
   *      are both discarded),
   *   2. prune label staging against the surviving drafts' reference set (the
   *      page-level generalization of the pipeline flow's `allLabels$`) and
   *      drop staged configs of pruned pending labels,
   *   3. consolidate labels (fail-fast) and EAGERLY remap the uuid map into
   *      the staged drafts and staged configs (retry-safe: a later-phase
   *      failure must not leave rows pointing at placeholder uuids),
   *   4. persist the surviving staged pipelines (fail-fast, per-row
   *      bookkeeping on `StagedPipelinesState`, assigned-ledger ids remapped
   *      incrementally),
   *   5. run the staged-resource walk (same prune + bottom-up persist as
   *      `consolidateResource`),
   *   6. save the method-type ROW — a create remaps the placeholder id through
   *      every ledger so a retry runs as an update — then replace the
   *      `ProtocolPipeline` join set with the final assigned set,
   *   7. best-effort label-config tail, then complete.
   *
   * Short-circuits when any non-idle phase is observed — an outer phase
   * (case-type) drives method-type consolidation internally.
   */
  public readonly consolidateMethodType = this.cascadeState.effect<void>((trigger$) =>
    trigger$.pipe(
      concatLatestFrom(() => this.cascadeState.phase$),
      filter(([, phase]) => phase === 'idle'),
      tap(() => this.cascadeState.setPhase('running-method-type')),
      concatLatestFrom(() => [
        this.stagedPipelinesState.pendingPipelines$,
        this.stagedPipelinesState.pipelineOverrides$,
        this.assignedPipelinesState.assignedIds$,
        this.labelState.pendingLabels$,
        this.labelState.labelOverrides$,
        this.labelConfigState.stagedConfigs$,
      ]),
      switchMap(
        ([, pendingPipelines, pipelineOverrides, assignedIds, pendingLabels, labelOverrides, stagedConfigs]) => {
          const survivingDrafts = this._pruneStalePipelineStaging(pendingPipelines, pipelineOverrides, assignedIds);
          const prunedPendingUuids = this._pruneStaleLabelStaging(
            this._collectDraftLabelUuids(survivingDrafts),
            pendingLabels,
            labelOverrides
          );
          this._pruneStaleLabelConfigs(prunedPendingUuids, stagedConfigs);

          return this._runLabelConsolidation().pipe(
            tap((consolidatedLabelMap) => {
              this.stagedPipelinesState.remapLabelUuids(consolidatedLabelMap);
              this.labelConfigState.remapLabelUuids(consolidatedLabelMap);
            }),
            switchMap(() =>
              this._runPipelineConsolidation().pipe(
                switchMap(() => this._runMethodTypeResourcePhase()),
                switchMap(() => this._runMethodTypeSaveAndJoins()),
                switchMap(() => this._runLabelConfigConsolidation())
              )
            ),
            tap(() => {
              this.cascadeState.setPhase('idle');
              this._cascadeCompleted$.next();
            }),
            catchError(() => {
              // Every fail-fast phase records its failure on its owner state
              // before (re)throwing; here the cascade just returns to idle.
              this.cascadeState.setPhase('idle');
              return EMPTY;
            })
          );
        }
      )
    )
  );

  /*
   * Pipeline-rooted top-level flow: prune stale label staging, consolidate
   * labels (fail-fast), remap the resulting old→new uuid map into pipeline
   * step drafts, save the pipeline (fail-fast), then run the label-config tail.
   * Short-circuits when any other phase is in progress.
   */
  public readonly consolidatePipeline = this.cascadeState.effect<void>((trigger$) =>
    trigger$.pipe(
      concatLatestFrom(() => this.cascadeState.phase$),
      filter(([, phase]) => phase === 'idle'),
      tap(() => this.cascadeState.setPhase('running-pipeline')),
      concatLatestFrom(() => [
        this.pipelineEditorState.allLabels$,
        this.labelState.pendingLabels$,
        this.labelState.labelOverrides$,
        this.labelConfigState.stagedConfigs$,
      ]),
      switchMap(([, allLabels, pendingLabels, labelOverrides, stagedConfigs]) => {
        const prunedPendingUuids = this._pruneStaleLabelStaging(allLabels, pendingLabels, labelOverrides);
        this._pruneStaleLabelConfigs(prunedPendingUuids, stagedConfigs);
        return this._runLabelConsolidation().pipe(
          // Eager remap into BOTH holders (drafts + staged configs): retry-safe —
          // if the pipeline save below fails, the persisted labels leave staging,
          // so a retry pass could never rebuild this map.
          tap((consolidatedLabelMap) => {
            this.pipelineEditorState.remapLabelUuids(consolidatedLabelMap);
            this.labelConfigState.remapLabelUuids(consolidatedLabelMap);
          }),
          concatLatestFrom(() => this.pipelineEditorState.draft$),
          switchMap(([, saveInput]) => {
            this.pipelineEditorState.setSaveException(null);
            this.pipelineEditorState.setSaving(true);
            return from(this.savePipelineCommand.execute(saveInput)).pipe(
              tap(() => this.pipelineEditorState.setSaving(false)),
              switchMap(() =>
                this._runLabelConfigConsolidation().pipe(
                  tap(() => {
                    this.cascadeState.setPhase('idle');
                    this._cascadeCompleted$.next();
                  })
                )
              ),
              catchError((error: Error) => {
                // pipeline-save failure → owner is pipelineEditorState.
                // Short-circuits before the label-config tail runs.
                this.pipelineEditorState.setSaveException(error);
                this.pipelineEditorState.setSaving(false);
                this.cascadeState.setPhase('idle');
                return EMPTY;
              })
            );
          }),
          catchError(() => {
            // Label-consolidation failure (fail-fast)
            // Nothing left to surface here — just return the cascade to idle
            this.cascadeState.setPhase('idle');
            return EMPTY;
          })
        );
      })
    )
  );

  /*
   * Resource-rooted top-level flow: prune stale resource staging against the
   * containment tree, then persist the staged ledger SEQUENTIALLY bottom-up
   * (children before parents). The ROOT resource is just another ledger entry —
   * the deepest parent persists last — so the page facade stages the root's
   * draft into the ledger BEFORE triggering this; the cascade has no dependency
   * on any editor state. The same staged-resource walk will later serve
   * consolidateMethodType. Short-circuits when any other phase is in progress.
   *
   * Resources have no other entity dependencies (no label remapping, no
   * best-effort tail), so the flow is a single prune → walk → complete sequence.
   */
  public readonly consolidateResource = this.cascadeState.effect<void>((trigger$) =>
    trigger$.pipe(
      concatLatestFrom(() => this.cascadeState.phase$),
      filter(([, phase]) => phase === 'idle'),
      tap(() => this.cascadeState.setPhase('running-resource')),
      concatLatestFrom(() => [
        this.assignedResourcesState.containers$,
        this.stagedResourcesState.pendingResources$,
        this.stagedResourcesState.resourceOverrides$,
      ]),
      switchMap(([, containers, pendingResources, resourceOverrides]) => {
        this._pruneStaleResourceStaging(containers, pendingResources, resourceOverrides);
        return this._runResourceConsolidation(containers).pipe(
          tap(() => {
            this.cascadeState.setPhase('idle');
            this._cascadeCompleted$.next();
          }),
          catchError(() => {
            // A row failed mid-walk. `_runResourceConsolidation` has already
            // recorded the failure, cleared `saving`, and left the failed row +
            // its dependents staged. Just return the cascade to idle.
            this.cascadeState.setPhase('idle');
            return EMPTY;
          })
        );
      })
    )
  );

  // ============================================================================
  // Private helpers — composed by the top-level phase methods above. They stay
  // private because a phase is only ever meaningful in sequence: each one is
  // entered with the ledgers its predecessors left behind. The one phase a
  // consumer legitimately wants on its own — the label-config tail — is also
  // available standalone from `LabelConfigSaveEffects`, which owns its own copy
  // of the loop for a consumer with a different post-batch policy.
  // ============================================================================

  /*
   * Returns the pruned PENDING uuids: those will never exist in the database,
   * so staged configs referencing them must be pruned too (see
   * `_pruneStaleLabelConfigs`). Pruned OVERRIDES are not returned — the label
   * itself persists under the original uuid, so its configs stay valid.
   */
  private _pruneStaleLabelStaging(
    allLabels: string[],
    pendingLabels: LabelData[],
    labelOverrides: Record<string, LabelOverride>
  ): string[] {
    const referenced = new Set(allLabels);
    const prunedPendingUuids: string[] = [];
    for (const pending of pendingLabels) {
      if (!referenced.has(pending.uuid)) {
        this.labelState.removePendingLabel(pending.uuid);
        prunedPendingUuids.push(pending.uuid);
      }
    }
    for (const uuid of Object.keys(labelOverrides)) {
      if (!referenced.has(uuid)) {
        this.labelState.removeLabelOverride(uuid);
      }
    }
    return prunedPendingUuids;
  }

  /*
   * Drop staged configs whose label was pruned as a never-persisted pending
   * row — saving them would write LabelConfig rows pointing at labels that
   * never got created.
   */
  private _pruneStaleLabelConfigs(prunedPendingUuids: string[], stagedConfigs: StagedLabelConfig[]): void {
    if (prunedPendingUuids.length === 0) return;
    const pruned = new Set(prunedPendingUuids);
    for (const config of stagedConfigs) {
      if (pruned.has(config.labelUuid)) {
        this.labelConfigState.removeConfig(config.stagingId);
      }
    }
  }

  /*
   * Shared tail for the parallel fail-fast consolidation batches (labels +
   * staged pipelines). Each task has already been wired to record its own
   * failure and resolve to a `BatchRowResult` rather than erroring, so a single
   * rejection cannot tear down the `forkJoin` and orphan the still-in-flight
   * siblings.
   *
   * `forkJoin([])` completes synchronously without emitting; `defaultIfEmpty`
   * supplies the empty result set. `observeOn(asapScheduler)` defers the emit to
   * the next microtask so a subscriber that triggers and immediately
   * re-subscribes can't race it.
   *
   * The first failed row rethrows to short-circuit the cascade (the row has
   * already recorded itself on its owner state); otherwise `saving` clears and
   * the successful row values are projected into the caller's return shape. The
   * `clearSaveExceptions`/`setSaving(true)` prologue stays with each caller so it
   * runs in the same turn as — and before — the per-row command executions.
   */
  private _runFailFastBatch<T, R>(
    state: ISavingState,
    tasks: Observable<BatchRowResult<T>>[],
    project: (values: T[]) => R
  ): Observable<R> {
    return forkJoin(tasks).pipe(
      defaultIfEmpty([] as BatchRowResult<T>[]),
      observeOn(asapScheduler),
      map((results) => {
        const failure = results.find((result): result is Extract<BatchRowResult<T>, { ok: false }> => !result.ok);
        if (failure) throw failure.error;
        state.setSaving(false);
        const values = results.flatMap((result) => (result.ok ? [result.value] : []));
        return project(values);
      }),
      catchError((error: Error) => {
        state.setSaving(false);
        return throwError(() => error);
      })
    );
  }

  /*
   * Bulk-save `pendingLabels` + `labelOverrides` returning a map with the final uuids.
   * Fail-fast: already-persisted rows stay persisted, failed rows stay staged for retry.
   *
   * Each per-row task resolves to a discriminated result instead of erroring,
   * so a single rejection can NOT tear down the `forkJoin` and orphan the
   * still-in-flight siblings
   */
  private _runLabelConsolidation(): Observable<Map<string, string>> {
    return combineLatest([this.labelState.pendingLabels$, this.labelState.labelOverrides$]).pipe(
      take(1),
      tap(() => {
        // Clear any failures from a prior attempt so this pass starts clean
        this.labelState.clearSaveExceptions();
        this.labelState.setSaving(true);
      }),
      switchMap(([pendingLabels, labelOverrides]) => {
        const tasks: Observable<BatchRowResult<readonly [string, string]>>[] = [
          ...pendingLabels.map((pending) =>
            this._runLabelSave(undefined, pending.languages).pipe(
              tap(() => this.labelState.removePendingLabel(pending.uuid)),
              map(
                (savedUuid): BatchRowResult<readonly [string, string]> => ({
                  ok: true,
                  value: [pending.uuid, savedUuid],
                })
              ),
              catchError((error: Error) => {
                this.labelState.recordSaveException({ uuid: pending.uuid, error });
                return of<BatchRowResult<readonly [string, string]>>({ ok: false, error });
              })
            )
          ),
          ...Object.values(labelOverrides).map(({ original, override }) =>
            this._runLabelSave(override.uuid, override.languages).pipe(
              tap(() => this.labelState.removeLabelOverride(original.uuid)),
              map(
                (savedUuid): BatchRowResult<readonly [string, string]> => ({
                  ok: true,
                  value: [original.uuid, savedUuid],
                })
              ),
              catchError((error: Error) => {
                this.labelState.recordSaveException({ uuid: original.uuid, error });
                return of<BatchRowResult<readonly [string, string]>>({ ok: false, error });
              })
            )
          ),
        ];

        return this._runFailFastBatch(this.labelState, tasks, (entries) => new Map(entries));
      })
    );
  }

  /*
   * Per-row label persist. Updates the paginated items list on success so
   * the standalone label-list views stay in sync.
   */
  private _runLabelSave(uuid: string | undefined, languages: LabelData['languages']): Observable<string> {
    return from(this.saveLabelCommand.execute({ uuid, values: languages })).pipe(
      map(({ uuid: savedUuid }) => savedUuid),
      tap((savedUuid) => this.labelState.updateLabelInList({ id: savedUuid, uuid: savedUuid, languages }))
    );
  }

  /*
   * Best-effort label-config tail. Rows persist exactly as staged: every flow
   * above remaps the shared ledger EAGERLY as soon as it learns a real label uuid
   * or method-type id, so there is no map left to apply here.
   *
   * Config failures must not abort the cascade after the entity that the colours
   * hang off is already persisted, hence best-effort: a rejection records itself
   * on the ledger, the row stays staged for the next page-level Save, and the
   * batch still settles.
   *
   * Returns a cold observable that emits once when the batch settles and then
   * completes, so the surrounding flows can sequence on it directly. `saving`
   * brackets the whole batch — the prologue runs in the same turn as the command
   * executions, so a UI bound to `saving$` never sees a gap.
   *
   * `LabelConfigSaveEffects` runs the same per-row loop for the labels page. The
   * duplication is deliberate: the post-batch policies genuinely differ (that
   * consumer discards whatever survives, having no page-level Save to retry
   * from), and sharing the loop is what previously forced this cascade to inject
   * a sibling Effects class and wait on a public trigger-and-wait observable.
   */
  private _runLabelConfigConsolidation(): Observable<void> {
    return this.labelConfigState.stagedConfigs$.pipe(
      take(1),
      tap(() => {
        // Clear any failures from a prior attempt so this pass starts clean
        this.labelConfigState.clearSaveExceptions();
        this.labelConfigState.setSaving(true);
      }),
      switchMap((stagedConfigs) => {
        const tasks: Observable<void>[] = stagedConfigs.map((config) => this._runLabelConfigSave(config));

        // `forkJoin([])` completes without emitting; `defaultIfEmpty` supplies
        // the empty result set so the settle below still runs for zero rows.
        return forkJoin(tasks).pipe(defaultIfEmpty([] as void[]));
      }),
      tap(() => this.labelConfigState.setSaving(false)),
      map(() => undefined)
    );
  }

  /*
   * Per-row config persist: unstage on success, record the failure keyed by the
   * `(label, method type)` pair on rejection and resolve anyway, so one rejection
   * cannot tear the batch down.
   */
  private _runLabelConfigSave(config: StagedLabelConfig): Observable<void> {
    return from(
      this.saveLabelConfigCommand.execute({
        configId: config.originalConfigId,
        labelUuid: config.labelUuid,
        methodTypeId: config.methodTypeId,
        color: config.color,
      })
    ).pipe(
      tap(() => this.labelConfigState.removeConfig(config.stagingId)),
      map(() => undefined),
      catchError((error: Error) => {
        this.labelConfigState.recordSaveException({
          labelUuid: config.labelUuid,
          methodTypeId: config.methodTypeId,
          error,
        });
        return of(undefined);
      })
    );
  }

  // ============================================================================
  // Method-type consolidation — pipeline-staging prune/persist + the row/joins
  // pair. Composed only by `consolidateMethodType` (and the future case-type
  // flow).
  // ============================================================================

  /*
   * Assignment-based pruning: any staged pipeline entry (pending draft OR
   * override of a persisted pipeline) whose id is not in the final assigned
   * set is unstaged — a created-then-unassigned draft is dropped entirely and
   * the edits of an unassigned persisted pipeline are discarded. Returns the
   * SURVIVING drafts, which feed the label reference set.
   */
  private _pruneStalePipelineStaging(
    pendingPipelines: StagedPipelineDraft[],
    pipelineOverrides: Record<string, StagedPipelineOverride>,
    assignedIds: ReadonlySet<string>
  ): StagedPipelineDraft[] {
    const surviving: StagedPipelineDraft[] = [];
    for (const draft of pendingPipelines) {
      if (assignedIds.has(draft.id)) surviving.push(draft);
      else this.stagedPipelinesState.unstage(draft.id);
    }
    for (const id of Object.keys(pipelineOverrides)) {
      if (assignedIds.has(id)) surviving.push(pipelineOverrides[id].draft);
      else this.stagedPipelinesState.unstage(id);
    }
    return surviving;
  }

  /*
   * Union of the label uuids referenced by the staged drafts' structured-step
   * categories — the page-level generalization of the pipeline editor's
   * `allLabels$` (which only covers the one open editing session).
   */
  private _collectDraftLabelUuids(drafts: StagedPipelineDraft[]): string[] {
    const uuids: string[] = [];
    for (const draft of drafts) {
      for (const step of draft.steps) {
        if (step.mode !== 'structured') continue;
        for (const category of step.categories) {
          uuids.push(category.uuid);
          for (const option of category.options) {
            uuids.push(option.uuid);
          }
        }
      }
    }
    return uuids;
  }

  /*
   * Builds the per-row save tasks for the staged pipeline batch (pending drafts
   * saved as CREATE, overrides as UPDATE). Label parity: each row resolves to a
   * discriminated result so one rejection cannot orphan in-flight siblings;
   * rows persisted before a sibling failure stay unstaged, failed rows stay
   * staged for retry. `onPendingSaved(stagedId, pipelineId)` lets the caller
   * record the tmp→real mapping however it needs — the single-method-type flow
   * remaps the current assigned slice eagerly; the case-type flow collects the
   * full map and applies it per slice in its row loop.
   */
  private _pipelineSaveTasks(
    pendingPipelines: StagedPipelineDraft[],
    pipelineOverrides: Record<string, StagedPipelineOverride>,
    onPendingSaved: (stagedId: string, pipelineId: string) => void
  ): Observable<BatchRowResult<void>>[] {
    return [
      ...pendingPipelines.map((draft) =>
        from(this.savePipelineCommand.execute({ ...draft, id: undefined })).pipe(
          tap(({ pipelineId }) => {
            this.stagedPipelinesState.unstage(draft.id);
            onPendingSaved(draft.id, pipelineId);
          }),
          map((): BatchRowResult<void> => ({ ok: true, value: undefined })),
          catchError((error: Error) => {
            this.stagedPipelinesState.recordSaveException({ id: draft.id, error });
            return of<BatchRowResult<void>>({ ok: false, error });
          })
        )
      ),
      ...Object.values(pipelineOverrides).map(({ draft }) =>
        from(this.savePipelineCommand.execute(draft)).pipe(
          tap(() => this.stagedPipelinesState.unstage(draft.id)),
          map((): BatchRowResult<void> => ({ ok: true, value: undefined })),
          catchError((error: Error) => {
            this.stagedPipelinesState.recordSaveException({ id: draft.id, error });
            return of<BatchRowResult<void>>({ ok: false, error });
          })
        )
      ),
    ];
  }

  /*
   * Bulk-save the staged pipeline drafts (post-prune, post-label-remap),
   * remapping each persisted draft's assigned-ledger entry in the CURRENT
   * context incrementally so the later join write reads REAL ids even if a
   * sibling row fails. Used by the single-method-type flow.
   */
  private _runPipelineConsolidation(): Observable<void> {
    return combineLatest([
      this.stagedPipelinesState.pendingPipelines$,
      this.stagedPipelinesState.pipelineOverrides$,
    ]).pipe(
      take(1),
      tap(() => {
        this.stagedPipelinesState.clearSaveExceptions();
        this.stagedPipelinesState.setSaving(true);
      }),
      switchMap(([pendingPipelines, pipelineOverrides]) => {
        const tasks = this._pipelineSaveTasks(pendingPipelines, pipelineOverrides, (stagedId, pipelineId) =>
          this.assignedPipelinesState.remapIds({ [stagedId]: pipelineId })
        );
        return this._runFailFastBatch<void, void>(this.stagedPipelinesState, tasks, () => undefined);
      })
    );
  }

  /*
   * The case-type variant of the staged-pipeline batch: instead of remapping a
   * single context's slice (a tmp pipeline belongs to whichever method type
   * assigned it, which may not be the current context), it RETURNS the full
   * tmp→real id map. The row loop applies it to each method type's slice after
   * switching context, just before reading the join set.
   */
  private _runPipelineConsolidationCollecting(): Observable<ReadonlyMap<string, string>> {
    return combineLatest([
      this.stagedPipelinesState.pendingPipelines$,
      this.stagedPipelinesState.pipelineOverrides$,
    ]).pipe(
      take(1),
      tap(() => {
        this.stagedPipelinesState.clearSaveExceptions();
        this.stagedPipelinesState.setSaving(true);
      }),
      switchMap(([pendingPipelines, pipelineOverrides]) => {
        const pipelineIdMap = new Map<string, string>();
        const tasks = this._pipelineSaveTasks(pendingPipelines, pipelineOverrides, (stagedId, pipelineId) =>
          pipelineIdMap.set(stagedId, pipelineId)
        );
        return this._runFailFastBatch<void, ReadonlyMap<string, string>>(
          this.stagedPipelinesState,
          tasks,
          () => pipelineIdMap
        );
      })
    );
  }

  /*
   * The same prune → bottom-up walk `consolidateResource` runs, packaged for
   * composition inside the method-type flow (which needs the staged resources
   * persisted — and the tree remapped to real ids — BEFORE the row save reads
   * the root's childIds as its resource pointer list).
   */
  private _runMethodTypeResourcePhase(): Observable<void> {
    return combineLatest([
      this.assignedResourcesState.containers$,
      this.stagedResourcesState.pendingResources$,
      this.stagedResourcesState.resourceOverrides$,
    ]).pipe(
      take(1),
      switchMap(([containers, pendingResources, resourceOverrides]) => {
        this._pruneStaleResourceStaging(containers, pendingResources, resourceOverrides);
        return this._runResourceConsolidation(containers);
      })
    );
  }

  /*
   * Persist the method-type ROW, then replace the `ProtocolPipeline` join set.
   * Two commands on purpose: the row save returns the real id FIRST and a
   * create immediately remaps the placeholder through EVERY holder (editor
   * state, resource tree root, assigned-pipelines context, staged configs) —
   * so a join failure leaves a retry that runs as an update instead of
   * duplicating the row, and the config tail reads real ids straight off the
   * ledger.
   */
  private _runMethodTypeSaveAndJoins(): Observable<void> {
    return combineLatest([
      this.methodTypeEditorState.draft$,
      this.methodTypeEditorState.persisted$,
      this.assignedResourcesState.containers$,
    ]).pipe(
      take(1),
      switchMap(([draft, persisted, containers]) => {
        this.methodTypeEditorState.setSaveException(null);
        this.methodTypeEditorState.setSaving(true);
        const draftId = draft.id ?? '';
        // Post-walk the tree holds real resource ids only; the root node's
        // children ARE the method type's resource pointer list.
        const resourceIds = containers[draftId]?.childIds ?? [];

        return from(
          this.saveMethodTypeCommand.execute({
            id: persisted ? draft.id : undefined,
            name: draft.name,
            assetTypeId: draft.assetTypeId,
            sampleNames: draft.sampleNames,
            config: draft.config, // present only in manual JSON mode
            acquisitionFields: draft.acquisitionFields,
            resourceIds,
          })
        ).pipe(
          map(({ methodTypeId }) => {
            if (!persisted && draftId !== '') {
              this.methodTypeEditorState.markPersisted(methodTypeId);
              this.assignedResourcesState.remapIds({ [draftId]: methodTypeId });
              // Staged pipelines were assigned under the temp draft id; move the
              // whole slice to the real id BEFORE reading `combinedAssigned$`.
              this.assignedPipelinesState.remapMethodTypeId({ from: draftId, to: methodTypeId });
              this.assignedPipelinesState.setContext({ methodTypeId, persisted: true });
              this.labelConfigState.remapMethodTypeId({ from: draftId, to: methodTypeId });
            }
            return methodTypeId;
          }),
          switchMap((methodTypeId) =>
            this.assignedPipelinesState.combinedAssigned$.pipe(
              take(1),
              switchMap((combinedAssigned) =>
                from(
                  this.saveAssignedPipelinesCommand.execute({
                    methodTypeId,
                    pipelineIds: combinedAssigned.map((pipeline) => pipeline.id),
                  })
                ).pipe(
                  tap(() => {
                    // Fold the session staging into the persisted snapshot so a
                    // continued session diffs against what is now in the DB.
                    this.assignedPipelinesState.setPersistedAssigned(combinedAssigned);
                    this.assignedPipelinesState.clearStaging();
                    this.methodTypeEditorState.setSaving(false);
                  }),
                  map(() => undefined)
                )
              )
            )
          ),
          catchError((error: Error) => {
            // Row or join failure → owner is the editor state. The row may
            // already be persisted (and remapped) — the retry runs as update.
            this.methodTypeEditorState.setSaveException(error);
            this.methodTypeEditorState.setSaving(false);
            return throwError(() => error);
          })
        );
      })
    );
  }

  // ============================================================================
  // Case-type consolidation — drives the method-type machinery once PER staged
  // method type, then persists the case-type row last. The label / pipeline /
  // resource / config phases are GLOBAL (one ledger across all method types) and
  // reuse the helpers above unchanged; only the row+joins loop and the prunes
  // below are case-type-specific.
  // ============================================================================

  /*
   * Assignment-based pruning of staged method types (pending drafts + overrides
   * of persisted method types) against the case type's final assigned set — the
   * `_pruneStalePipelineStaging` analogue. Returns the surviving entries paired
   * with whether each is an UPDATE (override of a persisted row) or a CREATE
   * (pending draft), which the row loop needs, plus — like
   * `_pruneStaleLabelStaging` — the pruned PENDING ids: those placeholders will
   * never exist in the database, so staged configs referencing them must go too
   * (see `_pruneStaleMethodTypeLabelConfigs`). Pruned OVERRIDES are not
   * returned: the method type itself persists under its real id.
   */
  private _pruneStaleMethodTypeStaging(
    pendingMethodTypes: StagedMethodTypeDraft[],
    methodTypeOverrides: Record<string, StagedMethodTypeOverride>,
    assignedIds: ReadonlySet<string>
  ): { survivors: { draft: StagedMethodTypeDraft; persisted: boolean }[]; prunedPendingIds: string[] } {
    const survivors: { draft: StagedMethodTypeDraft; persisted: boolean }[] = [];
    const prunedPendingIds: string[] = [];
    for (const draft of pendingMethodTypes) {
      if (assignedIds.has(draft.id)) survivors.push({ draft, persisted: false });
      else {
        this.stagedMethodTypesState.unstage(draft.id);
        prunedPendingIds.push(draft.id);
      }
    }
    for (const id of Object.keys(methodTypeOverrides)) {
      if (assignedIds.has(id)) survivors.push({ draft: methodTypeOverrides[id].draft, persisted: true });
      else this.stagedMethodTypesState.unstage(id);
    }
    return { survivors, prunedPendingIds };
  }

  /*
   * The pipeline prune reference set for the case type is the UNION of each
   * surviving method type's assigned pipeline ids (the single-method-type flow
   * uses just the one open context's set). Switches the assigned-pipelines
   * context per method type to read its slice; the per-method-type row loop
   * sets the definitive context again later, so this transient walk is safe.
   */
  private _collectSurvivingAssignedPipelineIds(survivingMethodTypeIds: string[]): Observable<ReadonlySet<string>> {
    if (survivingMethodTypeIds.length === 0) return of(new Set<string>());
    return from(survivingMethodTypeIds).pipe(
      concatMap((methodTypeId) => {
        this.assignedPipelinesState.setContext({ methodTypeId, persisted: true });
        return this.assignedPipelinesState.assignedIds$.pipe(take(1));
      }),
      toArray(),
      map((sets) => {
        const union = new Set<string>();
        for (const ids of sets) for (const id of ids) union.add(id);
        return union;
      })
    );
  }

  /*
   * Drop the staged resources owned by method types that were dropped in the
   * assignment prune. The resource walk keys its prune on `Object.keys(containers)`
   * and a dropped method type's container subtree is still in the tree (the
   * assignment prune touches only the method-type ledger), so without this its
   * resources would be persisted. Removes every staged ledger row in the subtree
   * rooted at each non-surviving method-type container.
   */
  private _pruneDroppedMethodTypeResourceSubtrees(
    containers: Record<string, ResourceContainer>,
    survivingMethodTypeIds: ReadonlySet<string>
  ): void {
    for (const [id, container] of Object.entries(containers)) {
      if (container.kind !== 'method-type' || survivingMethodTypeIds.has(id)) continue;
      for (const resourceId of this._collectSubtreeResourceIds(id, containers)) {
        this.stagedResourcesState.removePendingResource(resourceId);
        this.stagedResourcesState.removeResourceOverride(resourceId);
      }
    }
  }

  /* Collect every descendant node id under a container root (depth-first). */
  private _collectSubtreeResourceIds(rootId: string, containers: Record<string, ResourceContainer>): string[] {
    const ids: string[] = [];
    const visited = new Set<string>();
    const visit = (id: string): void => {
      for (const childId of containers[id]?.childIds ?? []) {
        if (visited.has(childId)) continue;
        visited.add(childId);
        ids.push(childId);
        visit(childId);
      }
    };
    visit(rootId);
    return ids;
  }

  /*
   * Drop staged label-configs whose method type will not exist on this case
   * type after the save. The test is EXISTENCE plus SESSION REMOVAL, not
   * case-type membership:
   *
   * | Row's method type                                      | Exists after save? | Decision |
   * | ------------------------------------------------------ | ------------------ | -------- |
   * | pending draft created then dropped this session         | no                 | prune    |
   * | PERSISTED-ASSIGNED, unassigned from the case type here  | yes                | prune    |
   * | anything else (never in this session's assignment set)  | yes                | KEEP     |
   *
   * The last row is why membership is the wrong predicate: the label-usage
   * query legitimately offers method types belonging to OTHER case types, and
   * pruning them made a colour the user had just confirmed vanish at save.
   *
   * Note the middle row's qualifier: `AssignedMethodTypesState.unassign` only
   * records ids that were in `persistedAssigned`, so a method type PICKED from
   * the catalog and then unassigned in the same session leaves no trace and is
   * indistinguishable here from a usage-query row. Its colour therefore rides
   * the save (last row). That is sound — a `LabelConfig` is a (label, method
   * type) fact, not a case-type-scoped one, and the method type exists — and
   * the alternative (recording every unassignment) would make an assign +
   * unassign round trip read as a change to the editor's dirty gate.
   */
  private _pruneStaleMethodTypeLabelConfigs(
    prunedPendingMethodTypeIds: readonly string[],
    stagedUnassignedMethodTypeIds: readonly string[],
    stagedConfigs: StagedLabelConfig[]
  ): void {
    const dropped = new Set([...prunedPendingMethodTypeIds, ...stagedUnassignedMethodTypeIds]);
    if (dropped.size === 0) return;
    for (const config of stagedConfigs) {
      if (dropped.has(config.methodTypeId)) {
        this.labelConfigState.removeConfig(config.stagingId);
      }
    }
  }

  /*
   * Persist the surviving staged method types SEQUENTIALLY (`concatMap`): each
   * iteration switches the shared assigned-pipelines context to its own slice,
   * so they must not overlap. Fail-fast: a row failure rethrows, the caller
   * returns the cascade to idle, and rows already persisted have been unstaged
   * so a retry does not duplicate them.
   */
  private _runStagedMethodTypeLoop(
    survivors: { draft: StagedMethodTypeDraft; persisted: boolean }[],
    pipelineIdMap: ReadonlyMap<string, string>
  ): Observable<void> {
    this.stagedMethodTypesState.clearSaveExceptions();
    this.stagedMethodTypesState.setSaving(true);
    return from(survivors).pipe(
      concatMap(({ draft, persisted }) => this._runStagedMethodTypeSaveAndJoins(draft, persisted, pipelineIdMap)),
      toArray(),
      tap(() => this.stagedMethodTypesState.setSaving(false)),
      map(() => undefined),
      catchError((error: Error) => {
        this.stagedMethodTypesState.setSaving(false);
        return throwError(() => error);
      })
    );
  }

  /*
   * The draft-driven sibling of `_runMethodTypeSaveAndJoins`: saves ONE staged
   * method type's row then replaces its `ProtocolPipeline` join set. Differs
   * from the editor-driven version only in its SOURCES (a `StagedMethodTypeDraft`,
   * not the open editor) and its CREATE remap targets (the staged + assigned
   * method-type ledgers, not the editor state). Also applies the case-type
   * pipeline id map to this method type's slice before reading the join set, so
   * tmp pipeline ids created in the global batch become real here.
   */
  private _runStagedMethodTypeSaveAndJoins(
    draft: StagedMethodTypeDraft,
    persisted: boolean,
    pipelineIdMap: ReadonlyMap<string, string>
  ): Observable<void> {
    return this.assignedResourcesState.containers$.pipe(
      take(1),
      switchMap((containers) => {
        const draftId = draft.id;
        // Post-walk the tree holds real resource ids; the root's children ARE
        // this method type's resource pointer list.
        const resourceIds = containers[draftId]?.childIds ?? [];

        return from(
          this.saveMethodTypeCommand.execute({
            id: persisted ? draftId : undefined,
            name: draft.name,
            assetTypeId: draft.assetTypeId,
            sampleNames: draft.sampleNames,
            config: draft.config, // present only in manual JSON mode
            acquisitionFields: draft.acquisitionFields,
            resourceIds,
          })
        ).pipe(
          switchMap(({ methodTypeId }) => {
            if (!persisted) {
              // Re-key every ledger from the placeholder to the real id so the
              // case-type row (and a retry) see the persisted method type.
              this.assignedMethodTypesState.remapIds({ [draftId]: methodTypeId });
              this.assignedResourcesState.remapIds({ [draftId]: methodTypeId });
              this.assignedPipelinesState.remapMethodTypeId({ from: draftId, to: methodTypeId });
              this.labelConfigState.remapMethodTypeId({ from: draftId, to: methodTypeId });
            }
            // Point the shared context at this method type's (now real) slice
            // and resolve any tmp pipeline ids the global batch just persisted.
            this.assignedPipelinesState.setContext({ methodTypeId, persisted: true });
            this.assignedPipelinesState.remapIds(Object.fromEntries(pipelineIdMap));

            return this.assignedPipelinesState.combinedAssigned$.pipe(
              take(1),
              switchMap((combinedAssigned) =>
                from(
                  this.saveAssignedPipelinesCommand.execute({
                    methodTypeId,
                    pipelineIds: combinedAssigned.map((pipeline) => pipeline.id),
                  })
                ).pipe(
                  tap(() => {
                    // Fold the session staging into the persisted snapshot and
                    // drop the staged method-type entry — a retry after a later
                    // failure re-reads the ledger without this (now persisted) row.
                    this.assignedPipelinesState.setPersistedAssigned(combinedAssigned);
                    this.assignedPipelinesState.clearStaging();
                    this.stagedMethodTypesState.unstage(draftId);
                  }),
                  map(() => undefined)
                )
              )
            );
          }),
          catchError((error: Error) => {
            // Row or join failure → owner is the staged-method-types ledger.
            this.stagedMethodTypesState.recordSaveException({ id: draft.id, error });
            return throwError(() => error);
          })
        );
      })
    );
  }

  /*
   * Persist the case-type ROW last (name/description/fields + the FINAL real
   * method-type ids). On success it marks the editor row persisted so a retry
   * runs as an update; on failure it records on both the editor row and the
   * case-data ledger (the owners the facade surfaces) and rethrows.
   */
  private _runCaseTypeRowSave(): Observable<void> {
    return combineLatest([
      this.caseTypeEditorState.draft$,
      this.caseTypeEditorState.persisted$,
      this.stagedCaseDataState.fields$,
      this.assignedMethodTypesState.combinedAssigned$,
    ]).pipe(
      take(1),
      switchMap(([draft, persisted, fields, combinedAssigned]) => {
        this.caseTypeEditorState.setSaveException(null);
        this.caseTypeEditorState.setSaving(true);
        this.stagedCaseDataState.setSaveException(null);
        this.stagedCaseDataState.setSaving(true);

        return from(
          this.saveCaseTypeCommand.execute({
            id: persisted ? draft.id : undefined,
            name: draft.name,
            description: draft.description,
            fields,
            methodTypeIds: combinedAssigned.map((methodType) => methodType.id),
          })
        ).pipe(
          tap(({ caseTypeId }) => {
            this.caseTypeEditorState.markPersisted(caseTypeId);
            this.caseTypeEditorState.setSaving(false);
            this.stagedCaseDataState.setSaving(false);
          }),
          map(() => undefined),
          catchError((error: Error) => {
            this.caseTypeEditorState.setSaveException(error);
            this.caseTypeEditorState.setSaving(false);
            this.stagedCaseDataState.setSaveException(error);
            this.stagedCaseDataState.setSaving(false);
            return throwError(() => error);
          })
        );
      })
    );
  }

  // ============================================================================
  // Resource consolidation — label parity, but the ledger is `pendingResources`
  // / `resourceOverrides` and the containment tree (`AssignedResourcesState`)
  // supplies relationships + the pruning reference set.
  // ============================================================================

  /*
   * Drop ledger rows the tree no longer references. Label parity: the reference
   * set is the tree's node ids (`Object.keys(containers)` — a node exists iff it
   * is a root or assigned somewhere). Any staged pending/override id absent from
   * the set is stale and removed before the walk.
   */
  private _pruneStaleResourceStaging(
    containers: Record<string, ResourceContainer>,
    pendingResources: ResourceDraftData[],
    resourceOverrides: Record<string, ResourceDraftOverride>
  ): void {
    const referenced = new Set(Object.keys(containers));
    for (const pending of pendingResources) {
      if (!referenced.has(pending.id)) {
        this.stagedResourcesState.removePendingResource(pending.id);
      }
    }
    for (const id of Object.keys(resourceOverrides)) {
      if (!referenced.has(id)) {
        this.stagedResourcesState.removeResourceOverride(id);
      }
    }
  }

  /*
   * Persist the staged resource ledger SEQUENTIALLY in BOTTOM-UP topological
   * order — children before parents, because staged resources nest (a draft mask
   * can contain draft responses) and a parent needs its children's REAL persisted
   * ids. Edges come from the tree (`containers[id].childIds`) restricted to the
   * staged id set; the walk resolves childIds through `idMap` over the captured
   * snapshot, so incremental remapping cannot confuse it.
   *
   * Fail-fast and sequential: on the first row failure we record the exception,
   * clear `saving`, and rethrow so the caller returns the cascade to idle. Rows
   * persisted before the failure stay persisted and unstaged; the failed row and
   * everything that still depends on it stay staged for a later retry.
   *
   * The ROOT resource is just another ledger entry (the deepest parent persists
   * last) — the page facade stages it before triggering, so there is nothing
   * root-specific here.
   *
   * Known limitation: a child assign/unassign under a CLEAN nested node
   * (`pendingPersistence` undefined → not in the ledger) is NOT persisted by this
   * flow, since only staged entries are saved. The future recursion work must
   * mark such parents `'update'` so they enter the ledger.
   */
  private _runResourceConsolidation(containers: Record<string, ResourceContainer>): Observable<void> {
    return combineLatest([
      this.stagedResourcesState.pendingResources$,
      this.stagedResourcesState.resourceOverrides$,
    ]).pipe(
      take(1),
      switchMap(([pendingResources, resourceOverrides]) => {
        this.stagedResourcesState.clearSaveExceptions();
        this.stagedResourcesState.setSaving(true);
        return from(this._persistResourcesBottomUp(containers, pendingResources, resourceOverrides));
      })
    );
  }

  private async _persistResourcesBottomUp(
    containers: Record<string, ResourceContainer>,
    pendingResources: ResourceDraftData[],
    resourceOverrides: Record<string, ResourceDraftOverride>
  ): Promise<void> {
    // Capture the ledger snapshot as `{ draft, pending }` entries keyed by their
    // in-memory id. Pending drafts carry no persisted id; overrides persist under
    // their own (real) id.
    const entries = new Map<string, { draft: ResourceDraftData; pending: boolean }>();
    for (const draft of pendingResources) entries.set(draft.id, { draft, pending: true });
    for (const id of Object.keys(resourceOverrides)) {
      entries.set(id, { draft: resourceOverrides[id].override, pending: false });
    }

    const order = this._bottomUpOrder([...entries.keys()], containers);

    // placeholderOrOldId -> persistedId, built incrementally as rows succeed.
    const idMap = new Map<string, string>();

    for (const id of order) {
      const entry = entries.get(id);
      if (!entry) continue;
      const { draft, pending } = entry;
      const childIds = (containers[id]?.childIds ?? []).map((childId) => idMap.get(childId) ?? childId);
      const input = {
        id: pending ? undefined : id,
        name: draft.name,
        type: draft.type,
        version: draft.version,
        description: draft.description ?? '',
        value: draft.value,
        displayName: draft.displayName,
        metadata: draft.metadata,
        file: draft.file,
        childIds,
      };

      try {
        const { resourceId } = await this.saveResourceCommand.execute(input);
        idMap.set(id, resourceId);
        if (pending) {
          this.stagedResourcesState.removePendingResource(id);
          // Remap the tree node id incrementally so it stays valid even if a
          // later row fails. The walk resolves childIds through `idMap` over the
          // captured snapshot, so this cannot confuse later rows.
          this.assignedResourcesState.remapIds({ [id]: resourceId });
        } else {
          this.stagedResourcesState.removeResourceOverride(id);
        }
      } catch (error) {
        this.stagedResourcesState.recordSaveException({ id, error: error as Error });
        this.stagedResourcesState.setSaving(false);
        throw error;
      }
    }

    this.stagedResourcesState.setSaving(false);
  }

  /*
   * Topological sort of the staged ids, children before parents. Edges are the
   * tree's `childIds` restricted to the staged set. Ids unknown to the tree (or
   * with no staged children) come out in their incoming order. Cycles cannot
   * occur in a containment tree; a defensive visited-guard keeps the recursion
   * finite regardless.
   */
  private _bottomUpOrder(stagedIds: string[], containers: Record<string, ResourceContainer>): string[] {
    const staged = new Set(stagedIds);
    const ordered: string[] = [];
    const placed = new Set<string>();
    const visiting = new Set<string>();

    const visit = (id: string): void => {
      if (placed.has(id) || visiting.has(id)) return;
      visiting.add(id);
      for (const childId of containers[id]?.childIds ?? []) {
        if (staged.has(childId)) visit(childId);
      }
      visiting.delete(id);
      placed.add(id);
      ordered.push(id);
    };

    for (const id of stagedIds) visit(id);
    return ordered;
  }
}
